import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Sparkles, CheckCircle2, Search, ArrowLeft, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';

interface AIBlogGeneratorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SERPAnalysis {
  averageWordCount: number;
  commonHeadings: {
    h2: string[];
    h3: string[];
  };
  lsiKeywords: string[];
  faqQuestions: string[];
  semanticClusters: Array<{
    topic: string;
    keywords: string[];
  }>;
  recommendedTone: string;
  topEntities: string[];
}

export function AIBlogGeneratorModal({ open, onOpenChange }: AIBlogGeneratorModalProps) {
  const [keyword, setKeyword] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [tone, setTone] = useState<'professional' | 'accessible' | 'technical'>('accessible');
  const [category, setCategory] = useState('');
  const [wordCountTarget, setWordCountTarget] = useState([1500]);
  
  const [currentPhase, setCurrentPhase] = useState<'input' | 'serp-preview' | 'generating'>('input');
  const [serpData, setSerpData] = useState<SERPAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [useCachedData, setUseCachedData] = useState(true);
  
  // Streaming state
  const [liveContent, setLiveContent] = useState('');
  const [streamingStage, setStreamingStage] = useState('');
  const [realProgress, setRealProgress] = useState(0);
  
  const [includeComponents, setIncludeComponents] = useState({
    hero: true,
    statsCards: true,
    faq: true,
    caseStudy: true,
    cta: true,
    importantNote: true,
  });

  const { toast } = useToast();
  const navigate = useNavigate();

  const handleAnalyzeSERP = async () => {
    if (!keyword || !targetAudience) {
      toast({
        title: 'Fehlende Angaben',
        description: 'Bitte Keyword und Zielgruppe angeben',
        variant: 'destructive'
      });
      return;
    }

    setIsAnalyzing(true);
    setProgress(0);
    setCurrentStage('Crawle Top-10 Google-Ergebnisse...');

    try {
      setProgress(30);
      
      const { data, error } = await supabase.functions.invoke('generate-blog-article', {
        body: {
          keyword,
          targetAudience,
          tone,
          includeComponents,
          category: category || undefined,
          wordCountTarget: wordCountTarget[0],
          stage: 'serp-only'
        }
      });

      if (error) throw error;
      if (!data || !data.success) {
        throw new Error(data?.error || 'SERP-Analyse fehlgeschlagen');
      }

      setProgress(100);
      setSerpData(data.serpData);
      setCurrentPhase('serp-preview');
      setCurrentStage('');

      toast({
        title: 'SERP-Analyse abgeschlossen',
        description: `${data.serpData.lsiKeywords.length} Keywords und ${data.serpData.faqQuestions.length} FAQ-Fragen gefunden.`
      });

    } catch (error: any) {
      console.error('SERP analysis error:', error);
      toast({
        title: 'SERP-Analyse fehlgeschlagen',
        description: error.message || 'Fehler bei der Analyse',
        variant: 'destructive'
      });
      setProgress(0);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerateWithSERP = async () => {
    if (!serpData) return;

    setIsGenerating(true);
    setCurrentPhase('generating');
    setLiveContent('');
    setRealProgress(0);
    setStreamingStage('Starte Generierung...');

    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
    const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    // Timeout detection
    const timeout = setTimeout(() => {
      if (realProgress === 0) {
        toast({
          title: "Verbindung fehlgeschlagen",
          description: "Der Server antwortet nicht. Bitte versuchen Sie es erneut.",
          variant: "destructive"
        });
        setIsGenerating(false);
      }
    }, 10000);

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-blog-article`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${SUPABASE_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          keyword,
          targetAudience,
          tone,
          includeComponents,
          category: category || undefined,
          wordCountTarget: wordCountTarget[0],
          serpData,
          skipCache: !useCachedData,
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6);

          try {
            const event = JSON.parse(jsonStr);

            switch (event.type) {
              case 'progress':
                setRealProgress(event.progress);
                setStreamingStage(event.message);
                break;

              case 'content-delta':
                setLiveContent(prev => prev + event.delta);
                setRealProgress(event.progress);
                break;

              case 'serp-complete':
                toast({
                  title: 'SERP-Analyse abgeschlossen',
                  description: `${event.serpData.keywordCount} Keywords gefunden`
                });
                break;

              case 'complete':
                setRealProgress(100);
                setStreamingStage('Fertig!');
                toast({
                  title: 'Artikel generiert!',
                  description: `"${event.blogPost.title}" wurde gespeichert.`
                });
                setTimeout(() => {
                  onOpenChange(false);
                  navigate(`/admin/blog/edit/${event.blogPost.id}`);
                }, 1500);
                break;

              case 'error':
                clearTimeout(timeout);
                toast({
                  title: "Fehler bei der Generierung",
                  description: event.error || "Ein unbekannter Fehler ist aufgetreten",
                  variant: "destructive"
                });
                throw new Error(event.error);
            }
          } catch (e) {
            console.error('SSE parse error:', e);
          }
        }
      }

      clearTimeout(timeout);
    } catch (error: any) {
      clearTimeout(timeout);
      console.error('Generation error:', error);
      
      let errorMessage = 'Ein unbekannter Fehler ist aufgetreten.';
      let errorDuration = 5000;
      
      if (error.message?.includes('timeout') || error.message?.includes('Timeout')) {
        errorMessage = 'Zeitüberschreitung. Versuchen Sie max. 1200 Wörter.';
        errorDuration = 7000;
      } else if (error.message?.includes('Token-Limit')) {
        errorMessage = 'Zu umfangreich. Versuchen Sie 800-1200 Wörter.';
        errorDuration = 6000;
      } else if (error.message?.includes('429')) {
        errorMessage = 'Zu viele Anfragen. Bitte in 1 Min. erneut versuchen.';
      } else if (error.message?.includes('402')) {
        errorMessage = 'API-Credits aufgebraucht.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: 'Generierung fehlgeschlagen',
        description: errorMessage,
        variant: 'destructive',
        duration: errorDuration
      });

      setRealProgress(0);
      setStreamingStage('');
    } finally {
      setIsGenerating(false);
    }
  };

  // Progress simulation for analyzing phase
  useEffect(() => {
    if (!isAnalyzing || progress >= 95) return;

    const timer = setTimeout(() => {
      setProgress(prev => Math.min(prev + 10, 95));
    }, 2000);

    return () => clearTimeout(timer);
  }, [isAnalyzing, progress]);

  // Progress simulation for generating phase
  useEffect(() => {
    if (!isGenerating || progress >= 95) return;

    const stages = [
      { progress: 50, text: 'Erstelle HTML-Struktur...' },
      { progress: 70, text: 'Generiere visuelle Komponenten...' },
      { progress: 90, text: 'Speichere in Datenbank...' },
    ];

    let currentIndex = stages.findIndex(s => s.progress > progress);
    if (currentIndex === -1) return;

    const timer = setTimeout(() => {
      setProgress(stages[currentIndex].progress);
      setCurrentStage(stages[currentIndex].text);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isGenerating, progress]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            KI-Blog-Artikel generieren
          </DialogTitle>
          <DialogDescription>
            2-Phasen-Workflow: SERP-Analyse → Vorschau → Content-Generierung
          </DialogDescription>
        </DialogHeader>

        {/* Progress Stepper */}
        <div className="flex items-center justify-center gap-2 py-4 border-b">
          <div className={`flex items-center gap-2 ${currentPhase === 'input' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold ${currentPhase === 'input' ? 'border-primary' : 'border-muted'}`}>
              1
            </div>
            <span className="text-sm font-medium hidden sm:inline">Eingabe</span>
          </div>
          
          <div className={`w-8 sm:w-12 h-0.5 ${currentPhase !== 'input' ? 'bg-primary' : 'bg-border'}`}></div>
          
          <div className={`flex items-center gap-2 ${currentPhase === 'serp-preview' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold ${currentPhase === 'serp-preview' ? 'border-primary' : 'border-muted'}`}>
              {currentPhase === 'serp-preview' ? <CheckCircle2 className="w-5 h-5" /> : '2'}
            </div>
            <span className="text-sm font-medium hidden sm:inline">SERP</span>
          </div>
          
          <div className={`w-8 sm:w-12 h-0.5 ${currentPhase === 'generating' ? 'bg-primary' : 'bg-border'}`}></div>
          
          <div className={`flex items-center gap-2 ${currentPhase === 'generating' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold ${currentPhase === 'generating' ? 'border-primary' : 'border-muted'}`}>
              3
            </div>
            <span className="text-sm font-medium hidden sm:inline">Artikel</span>
          </div>
        </div>

        <div className="space-y-6 py-4">
          {/* Input Phase */}
          {currentPhase === 'input' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="keyword">Keyword *</Label>
                <Input
                  id="keyword"
                  placeholder="z.B. Ransomware-Schutz Zahnarztpraxis"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  disabled={isAnalyzing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="audience">Zielgruppe *</Label>
                <Select value={targetAudience} onValueChange={setTargetAudience} disabled={isAnalyzing}>
                  <SelectTrigger id="audience">
                    <SelectValue placeholder="Zielgruppe wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Hausärzte">Hausärzte</SelectItem>
                    <SelectItem value="Zahnärzte">Zahnärzte</SelectItem>
                    <SelectItem value="Fachärzte">Fachärzte</SelectItem>
                    <SelectItem value="MVZ">MVZ (Medizinische Versorgungszentren)</SelectItem>
                    <SelectItem value="Kliniken">Kliniken & Krankenhäuser</SelectItem>
                    <SelectItem value="Praxen allgemein">Praxen allgemein</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tone">Ton / Stil</Label>
                <Select value={tone} onValueChange={(v: any) => setTone(v)} disabled={isAnalyzing}>
                  <SelectTrigger id="tone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional (formell, sachlich)</SelectItem>
                    <SelectItem value="accessible">Accessible (zugänglich, verständlich)</SelectItem>
                    <SelectItem value="technical">Technical (technisch, detailliert)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Kategorie (optional)</Label>
                <Input
                  id="category"
                  placeholder="z.B. Cyberversicherung, Schadensfall"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={isAnalyzing}
                />
              </div>

              <div className="space-y-2">
                <Label>Ziel-Wortanzahl: {wordCountTarget[0]} Wörter</Label>
                <Slider
                  value={wordCountTarget}
                  onValueChange={setWordCountTarget}
                  min={1000}
                  max={3000}
                  step={100}
                  disabled={isAnalyzing}
                  className="w-full"
                />
              </div>

              <div className="space-y-3">
                <Label>Visuelle Komponenten</Label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(includeComponents).map(([key, value]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Checkbox
                        id={key}
                        checked={value}
                        onCheckedChange={(checked) =>
                          setIncludeComponents({ ...includeComponents, [key]: !!checked })
                        }
                        disabled={isAnalyzing}
                      />
                      <label htmlFor={key} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {key === 'hero' && 'Hero-Sektion'}
                        {key === 'statsCards' && 'Info-Cards'}
                        {key === 'faq' && 'FAQ-Accordion'}
                        {key === 'caseStudy' && 'Praxis-Fallbeispiel'}
                        {key === 'cta' && 'CTA-Box'}
                        {key === 'importantNote' && 'Important-Alert'}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <Checkbox 
                  id="use-cache"
                  checked={useCachedData}
                  onCheckedChange={(checked) => setUseCachedData(!!checked)}
                  disabled={isAnalyzing}
                />
                <Label htmlFor="use-cache" className="text-sm cursor-pointer flex-1">
                  Cached data verwenden (schneller & günstiger)
                </Label>
              </div>

              {isAnalyzing && (
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{currentStage}</span>
                    <span className="font-medium">{progress}%</span>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2.5">
                    <div
                      className="bg-primary h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isAnalyzing}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleAnalyzeSERP}
                  disabled={isAnalyzing || !keyword || !targetAudience}
                  className="flex-1"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analysiere...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      SERP analysieren
                    </>
                  )}
                </Button>
              </div>
            </>
          )}

          {/* SERP Preview Phase */}
          {currentPhase === 'serp-preview' && serpData && (
            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  SERP-Analyse abgeschlossen
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCurrentPhase('input');
                    setSerpData(null);
                    setProgress(0);
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Zurück
                </Button>
              </div>

              <div className="bg-primary/5 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Durchschnittliche Wortanzahl (Top-10)</span>
                  <span className="text-2xl font-bold text-primary">{serpData.averageWordCount}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>LSI-Keywords ({serpData.lsiKeywords.length})</Label>
                <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-lg max-h-32 overflow-y-auto">
                  {serpData.lsiKeywords.map((kw, i) => (
                    <span key={i} className="px-2 py-1 bg-background border rounded text-xs">
                      {kw}
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Häufige H2-Überschriften ({serpData.commonHeadings.h2.length})</Label>
                <ul className="space-y-1 p-3 bg-muted rounded-lg max-h-40 overflow-y-auto">
                  {serpData.commonHeadings.h2.slice(0, 10).map((h, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <span className="text-muted-foreground">•</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-2">
                <Label>FAQ-Fragen ({serpData.faqQuestions.length})</Label>
                <ul className="space-y-1 p-3 bg-muted rounded-lg max-h-40 overflow-y-auto">
                  {serpData.faqQuestions.slice(0, 8).map((q, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <span className="text-primary">?</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {serpData.semanticClusters.length > 0 && (
                <div className="space-y-2">
                  <Label>Themen-Cluster ({serpData.semanticClusters.length})</Label>
                  <div className="grid gap-2 p-3 bg-muted rounded-lg">
                    {serpData.semanticClusters.map((cluster, i) => (
                      <div key={i} className="bg-background p-2 rounded">
                        <div className="font-medium text-sm mb-1">{cluster.topic}</div>
                        <div className="flex flex-wrap gap-1">
                          {cluster.keywords.map((kw, j) => (
                            <span key={j} className="text-xs text-muted-foreground">
                              {kw}{j < cluster.keywords.length - 1 ? ',' : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-amber-50 dark:bg-amber-950 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">Empfohlener Ton aus SERP-Analyse:</span>
                  <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900 rounded text-sm font-semibold">
                    {serpData.recommendedTone}
                  </span>
                </div>
                {serpData.recommendedTone !== tone && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Hinweis: Sie haben "{tone}" gewählt, SERP empfiehlt "{serpData.recommendedTone}"
                  </p>
                )}
              </div>

              <Button
                onClick={handleGenerateWithSERP}
                disabled={isGenerating}
                className="w-full"
                size="lg"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generiere Artikel...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Artikel mit diesen SERP-Daten generieren
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Generating Phase with Live Preview */}
          {currentPhase === 'generating' && (
            <div className="space-y-4">
              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{streamingStage || 'Generiere Artikel...'}</span>
                  <span className="font-semibold">{Math.round(realProgress)}%</span>
                </div>
                <div className="w-full bg-secondary rounded-full h-3">
                  <div
                    className="bg-primary h-3 rounded-full transition-all duration-300"
                    style={{ width: `${realProgress}%` }}
                  />
                </div>
              </div>

              {/* Live Content Preview mit exakt denselben Prose-Klassen wie BlogPost.tsx */}
              {liveContent && (
                <div className="border rounded-lg p-6 bg-card/50 max-h-[500px] overflow-y-auto">
                  <h4 className="text-sm font-semibold mb-4 text-primary flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Live-Vorschau (formatierter Content)
                  </h4>
                  
                  {/* Exakt dieselben Prose-Klassen wie in BlogPost.tsx */}
                  <div 
                    className="prose prose-lg dark:prose-invert max-w-none
                      prose-headings:text-foreground
                      prose-h1:text-4xl prose-h1:font-bold prose-h1:mb-6 prose-h1:mt-8
                      prose-h2:text-3xl prose-h2:font-semibold prose-h2:mb-4 prose-h2:mt-8
                      prose-h3:text-2xl prose-h3:font-medium prose-h3:mb-3 prose-h3:mt-6
                      prose-p:text-lg prose-p:leading-relaxed prose-p:mb-4 prose-p:text-foreground
                      prose-a:text-accent hover:prose-a:text-accent/80 prose-a:no-underline hover:prose-a:underline
                      prose-strong:font-bold prose-strong:text-foreground
                      prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                      prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
                      prose-li:my-2 prose-li:text-foreground
                      prose-img:rounded-lg prose-img:border prose-img:my-6
                      prose-blockquote:border-l-4 prose-blockquote:border-accent 
                      prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground
                      prose-code:text-accent prose-code:bg-muted prose-code:px-1 prose-code:rounded
                      prose-pre:bg-muted prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(liveContent, {
                        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre', 'div', 'span'],
                        ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel']
                      })
                    }}
                  />
                  
                  {/* Typing Indicator am Ende */}
                  {isGenerating && (
                    <div className="flex items-center gap-1 mt-4 pt-4 border-t border-border">
                      <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                      <span className="text-xs text-muted-foreground ml-2">Generiere weiter...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
